import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';

interface SyncDeviceData {
  external_id: string;
  name: string;
  price: number;
  description?: string;
  manufacturer?: string;
  model?: string;
  is_active?: boolean;
}

interface SyncDeviceResponse {
  success: boolean;
  data: {
    device_id: string;
    is_new: boolean;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface TrackingProduct {
  id: string;
  name: string;
  price_cents: number;
  description?: string;
  manufacturer?: string;
  model?: string;
  is_active: boolean;
  console_device_id?: string | null;
  console_synced_at?: string | null;
}

export function useConsoleDevices() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Always configured - credentials are on the server
  const isConfigured = true;

  const syncDevice = async (product: TrackingProduct): Promise<SyncDeviceResponse['data'] | null> => {
    const deviceData: SyncDeviceData = {
      external_id: product.id,
      name: product.name,
      price: product.price_cents, // Já está em centavos
      description: product.description,
      manufacturer: product.manufacturer,
      model: product.model,
      is_active: product.is_active,
    };

    const { data, error: invokeError } = await supabase.functions.invoke('console-sync-device', {
      body: deviceData,
    });

    if (invokeError) {
      throw new Error(invokeError.message || 'Erro ao chamar função de sincronização');
    }

    const response = data as SyncDeviceResponse;

    if (!response.success) {
      throw new Error(response.error?.message || 'Erro ao sincronizar dispositivo');
    }

    return response.data;
  };

  const syncSingleDevice = async (product: TrackingProduct) => {
    setLoading(true);
    setError(null);

    try {
      const result = await syncDevice(product);
      
      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['tracking-products'] });
      
      toast({
        title: 'Dispositivo sincronizado!',
        description: `${product.name} foi sincronizado com a Console.`,
      });
      
      return result;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Erro na sincronização',
        description: err.message,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncAllDevices = async (products: TrackingProduct[]) => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: products.length });

    const results: { success: TrackingProduct[]; failed: { product: TrackingProduct; error: string }[] } = {
      success: [],
      failed: [],
    };

    try {
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        setProgress({ current: i + 1, total: products.length });

        try {
          await syncDevice(product);
          results.success.push(product);
        } catch (err: any) {
          results.failed.push({ product, error: err.message });
        }
      }

      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['tracking-products'] });

      if (results.failed.length === 0) {
        toast({
          title: 'Sincronização concluída!',
          description: `${results.success.length} dispositivo(s) sincronizado(s) com sucesso.`,
        });
      } else if (results.success.length > 0) {
        toast({
          title: 'Sincronização parcial',
          description: `${results.success.length} sucesso, ${results.failed.length} falha(s).`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Falha na sincronização',
          description: `Todos os ${results.failed.length} dispositivos falharam.`,
          variant: 'destructive',
        });
      }

      return results;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return {
    loading,
    error,
    progress,
    isConfigured,
    syncSingleDevice,
    syncAllDevices,
  };
}
